// /admin (Operator overview) — dashboard. Replaces the four "—" placeholder
// KPI cards with real numbers from GET /api/admin/summary, plus a 30-day
// cleared-payments sparkline, outstanding-by-currency, and a recent-activity
// strip.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import { apiGet, ApiError } from '../../lib/api';
import { Sparkline } from '../../components/Sparkline';

interface SummaryRow {
  inbox_unread: number;
  active_engagements: number;
  pipeline_value_pence: number;
  active_clients: number;
  outstanding_by_currency: Array<{ currency: string; amount_minor: number }>;
  cleared_this_month: {
    count: number;
    by_currency: Array<{ currency: string; amount_minor: number }>;
  };
  cleared_30d_spark: Array<{ date: string; count: number }>;
  recent_audit: Array<{
    id: string;
    kind: string;
    actor_name: string;
    actor_kind: 'staff' | 'client' | 'system';
    what: string;
    at: string;
    resource: string | null;
    resource_id: string | null;
  }>;
}

function formatMoney(amount_minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(amount_minor / 100);
  } catch {
    return `${currency} ${(amount_minor / 100).toLocaleString()}`;
  }
}

// Compact relative-time formatter for the recent-activity strip. We don't
// want full timestamps in a feed — readers want "5m ago" / "yesterday".
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86_400) return `${Math.round(diffSec / 86_400)}d ago`;
  return iso.slice(0, 10);
}

// Where does each audit row link to? Best-effort — only deep-link kinds we
// can route to confidently. Everything else links to the audit log itself.
function auditHref(row: SummaryRow['recent_audit'][number]): string {
  if (row.resource === 'requests' && row.resource_id) return `/admin/requests/${row.resource_id}`;
  if (row.resource === 'proposals' && row.resource_id) return `/admin/proposals/${row.resource_id}`;
  if (row.resource === 'invoices' && row.resource_id) return `/admin/invoices/${row.resource_id}`;
  if (row.resource === 'clients' && row.resource_id) return `/admin/clients/${row.resource_id}`;
  return '/admin/audit';
}

export function AdminOverview() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<SummaryRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<SummaryRow>('/api/admin/summary')
      .then(setSummary)
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
  }, []);

  // Pipeline value displayed in GBP — declared brief value_pence is stored
  // in the same minor-unit currency the brief used (we don't enforce GBP
  // there); for v1 dashboard purposes we treat it as a single bucket. A
  // multi-currency pipeline view ships with the dashboard "by currency"
  // breakout below the headline.
  const pipelineDisplay = summary
    ? formatMoney(summary.pipeline_value_pence, 'GBP')
    : '—';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="portal-h1">Operator overview</h1>
          <p className="portal-sub">
            Signed in as <strong style={{ color: 'var(--fg)' }}>{user?.name}</strong>{' '}
            ({user?.role}) · {user?.office}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link className="btn ghost" to="/admin/audit">Audit log</Link>
          <Link className="btn accent" to="/admin/inbox">Open inbox →</Link>
        </div>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {/* ── Headline KPIs ─────────────────────────────────────────────── */}
      <div className="portal-cards">
        <Link to="/admin/inbox" className="card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="lbl">Inbox · unread</div>
          <div className="val">{summary?.inbox_unread ?? '—'}</div>
          <div className="delta">Untriaged briefs</div>
        </Link>
        <Link to="/admin/pipeline" className="card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="lbl">Active engagements</div>
          <div className="val">{summary?.active_engagements ?? '—'}</div>
          <div className="delta">In delivery</div>
        </Link>
        <Link to="/admin/pipeline" className="card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="lbl">Pipeline value</div>
          <div className="val" style={{ fontSize: 22 }}>{pipelineDisplay}</div>
          <div className="delta">Open briefs · declared value</div>
        </Link>
        <Link to="/admin/clients" className="card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="lbl">Active clients</div>
          <div className="val">{summary?.active_clients ?? '—'}</div>
          <div className="delta">Body corporate · status=active</div>
        </Link>
      </div>

      {/* ── Cleared 30d spark + outstanding by currency ───────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 16,
          marginTop: 16,
        }}
      >
        <div className="card">
          <div className="lbl">Cleared payments · 30 days</div>
          <div style={{ marginTop: 8 }}>
            {summary ? (
              <>
                <Sparkline
                  data={summary.cleared_30d_spark}
                  ariaLabel="Cleared payments per day for the last 30 days"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                  <span>{summary.cleared_30d_spark[0]?.date ?? ''}</span>
                  <span>{summary.cleared_30d_spark[summary.cleared_30d_spark.length - 1]?.date ?? ''}</span>
                </div>
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <strong>{summary.cleared_30d_spark.reduce((s, d) => s + d.count, 0)}</strong>
                  <span style={{ color: 'var(--fg-mute)' }}> succeeded payments in window</span>
                </div>
              </>
            ) : (
              <div style={{ height: 48, color: 'var(--fg-mute)', fontSize: 12 }}>Loading…</div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="lbl">Cleared this month</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            {summary?.cleared_this_month.by_currency.length === 0 && (
              <span style={{ color: 'var(--fg-mute)', fontSize: 12 }}>Nothing cleared yet</span>
            )}
            {summary?.cleared_this_month.by_currency.map((r) => (
              <div key={r.currency} style={{ display: 'flex', justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ color: 'var(--fg-mute)' }}>{r.currency}</span>
                <strong>{formatMoney(r.amount_minor, r.currency)}</strong>
              </div>
            ))}
            {summary && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-mute)' }}>
                {summary.cleared_this_month.count} payment{summary.cleared_this_month.count === 1 ? '' : 's'}
                {' · '}
                <Link to="/admin/transactions" style={{ borderBottom: '1px solid var(--rule)' }}>view ledger →</Link>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="lbl">Outstanding · by currency</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            {summary?.outstanding_by_currency.length === 0 && (
              <span style={{ color: 'var(--fg-mute)', fontSize: 12 }}>Nothing outstanding</span>
            )}
            {summary?.outstanding_by_currency.map((r) => (
              <div key={r.currency} style={{ display: 'flex', justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ color: 'var(--fg-mute)' }}>{r.currency}</span>
                <strong>{formatMoney(r.amount_minor, r.currency)}</strong>
              </div>
            ))}
            {summary && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-mute)' }}>
                <Link to="/admin/finance" style={{ borderBottom: '1px solid var(--rule)' }}>finance →</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Recent activity strip ────────────────────────────────────── */}
      <div className="card flush" style={{ marginTop: 16 }}>
        <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Recent activity</h3>
          <Link to="/admin/audit" className="btn sm ghost">Full log →</Link>
        </div>
        <table className="tbl">
          <tbody>
            {summary === null && (
              <tr><td colSpan={3} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {summary && summary.recent_audit.length === 0 && (
              <tr>
                <td colSpan={3} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 24 }}>
                  No audit activity yet.
                </td>
              </tr>
            )}
            {summary?.recent_audit.map((a) => (
              <tr key={a.id}>
                <td style={{ fontSize: 12, color: 'var(--fg-mute)', whiteSpace: 'nowrap', fontFamily: 'var(--mono)' }}>
                  {relativeTime(a.at)}
                </td>
                <td style={{ fontSize: 13 }}>
                  <strong>{a.actor_name}</strong>
                  <span style={{ color: 'var(--fg-mute)', fontSize: 11, marginLeft: 6 }}>{a.actor_kind}</span>
                </td>
                <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>
                  <Link to={auditHref(a)}>{a.what}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
